import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calculator,
  Receipt,
  Users,
  Wallet,
  BarChart3,
  PieChart,
  RefreshCcw,
  FileDigit,
  ShieldCheck,
  Globe2,
  CloudCog,
  Coins,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const serviceIcons = {
  muhasebe: Calculator,
  vergi: Receipt,
  sgk: Users,
  bordro: Wallet,
  rapor: BarChart3,
  butce: PieChart,
  kdv: RefreshCcw,
  edefter: FileDigit,
};

const Home = () => {
  const { t } = useLanguage();

  const stats = [
    { value: "20+", label: t.home.statsExp },
    { value: "98%", label: t.home.statsRetention },
  ];

  const whys = [
    { icon: ShieldCheck, title: t.home.why1Title, desc: t.home.why1Desc },
    { icon: Globe2, title: t.home.why2Title, desc: t.home.why2Desc },
    { icon: CloudCog, title: t.home.why3Title, desc: t.home.why3Desc },
    { icon: Coins, title: t.home.why4Title, desc: t.home.why4Desc },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-90"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-rotam-bg via-rotam-bg/95 to-rotam-bg/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-rotam-bg via-transparent to-transparent" />

        <div className="container-rotam relative grid lg:grid-cols-12 gap-10 pt-24 pb-28 md:pt-32 md:pb-36 lg:pt-40 lg:pb-44">
          <div className="lg:col-span-7 animate-fade-up">
            <p className="overline mb-6" data-testid="hero-overline">{t.home.overline}</p>
            <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-rotam-ink">
              {t.home.heroTitle}
            </h1>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-rotam-muted max-w-xl">
              {t.home.heroSubtitle}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                {t.home.heroCta} <ArrowRight size={18} />
              </Link>
              <Link to="/services" data-testid="hero-cta-secondary" className="btn-ghost">
                {t.home.heroSecondary}
              </Link>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="container-rotam relative -mt-12 md:-mt-16 mb-10 z-10">
          <div className="bg-white border border-rotam-border rounded-sm shadow-sm grid grid-cols-2 divide-x divide-rotam-border max-w-2xl">
            {stats.map((s, i) => (
              <div key={i} className="p-8 md:p-10" data-testid={`stat-${i}`}>
                <div className="font-heading text-3xl md:text-4xl font-medium text-rotam-primary">{s.value}</div>
                <div className="mt-2 text-xs tracking-[0.18em] uppercase text-rotam-muted">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad" data-testid="services-section">
        <div className="container-rotam">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <p className="overline">{t.home.servicesOverline}</p>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-light leading-tight text-rotam-ink">
                {t.home.servicesTitle}
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-rotam-muted text-base leading-relaxed">{t.home.servicesDesc}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-rotam-border border border-rotam-border">
            {t.services.items.map((s, i) => {
              const Icon = serviceIcons[s.key] || Calculator;
              return (
                <div
                  key={s.key}
                  data-testid={`service-card-${s.key}`}
                  className="bg-white p-8 md:p-10 group hover:bg-rotam-primary transition-colors duration-500 cursor-default"
                >
                  <Icon
                    size={28}
                    strokeWidth={1.5}
                    className="text-rotam-primary group-hover:text-rotam-accent transition-colors"
                  />
                  <h3 className="mt-8 font-heading text-xl font-medium text-rotam-ink group-hover:text-white transition-colors">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-rotam-muted group-hover:text-white/80 transition-colors">
                    {s.desc}
                  </p>
                  <div className="mt-6 text-xs tracking-[0.2em] uppercase opacity-0 group-hover:opacity-100 text-rotam-accent transition-opacity">
                    0{i + 1}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-pad bg-white" data-testid="why-section">
        <div className="container-rotam grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <img
              src="https://images.pexels.com/photos/210607/pexels-photo-210607.jpeg"
              alt="ROTAM office"
              className="w-full h-[420px] md:h-[520px] object-cover rounded-sm"
            />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 lg:pl-8">
            <p className="overline">{t.home.whyOverline}</p>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-light leading-tight text-rotam-ink">
              {t.home.whyTitle}
            </h2>
            <p className="mt-6 text-rotam-muted leading-relaxed">{t.home.whyDesc}</p>

            <div className="mt-10 grid sm:grid-cols-2 gap-8">
              {whys.map((w, i) => (
                <div key={i} className="flex gap-4" data-testid={`why-${i}`}>
                  <div className="shrink-0 w-10 h-10 bg-rotam-primary/10 text-rotam-primary flex items-center justify-center rounded-sm">
                    <w.icon size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-medium text-rotam-ink">{w.title}</h3>
                    <p className="mt-2 text-sm text-rotam-muted leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="section-pad" data-testid="industries-section">
        <div className="container-rotam">
          <div className="max-w-3xl">
            <p className="overline">{t.home.industriesOverline}</p>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-light leading-tight text-rotam-ink">
              {t.home.industriesTitle}
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-rotam-border border border-rotam-border">
            {t.home.industries.map((ind, i) => (
              <div
                key={i}
                data-testid={`industry-${i}`}
                className="bg-rotam-bg p-8 hover:bg-white transition-colors text-sm md:text-base font-heading font-medium text-rotam-ink"
              >
                <span className="block text-[10px] tracking-[0.2em] uppercase text-rotam-accent mb-2">
                  0{i + 1}
                </span>
                {ind}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-rotam-primary text-white relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-rotam-accent rounded-full blur-3xl" />
        </div>
        <div className="container-rotam relative grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
              {t.home.ctaTitle}
            </h2>
            <p className="mt-5 text-white/80 leading-relaxed max-w-2xl">{t.home.ctaDesc}</p>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="inline-flex items-center gap-2 bg-rotam-accent text-rotam-primary px-8 py-4 rounded-sm font-medium tracking-wide hover:bg-white transition-colors"
            >
              {t.home.ctaButton} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
